/**
 * Renders the local queue of inbox-route submissions awaiting drain.
 *
 * Each entry was created when the user typed during a streaming run
 * and the wrapped `submit()` posted to `POST /threads/:id/inbox`. The
 * queued entry stays visible until the SDK echoes back a real message
 * with `additional_kwargs.inboxRowId` matching this entry — at which
 * point the snapshot-sync in `useLanggraphStream` drops it and the
 * canonical transcript message takes over.
 *
 * Drop-in usage:
 *   <Chat.Messages.List>
 *     {turns.map(...)}
 *     <Chat.QueuedMessages />
 *     {isStreaming && <ThinkingIndicator />}
 *   </Chat.Messages.List>
 *
 * Renders nothing when the queue is empty.
 */
import { twMerge } from "tailwind-merge";
import type { ReactNode } from "react";
import { useChatQueuedMessages } from "../ChatContext";
import { UserMessage } from "./UserMessage";
import type { QueuedMessage } from "../../types";

export interface QueuedMessagesProps {
  /**
   * Optional class name applied to each queued message wrapper. The
   * inner <UserMessage> uses the standard user-bubble styling — wrap
   * here to add a "delivering..." badge, fade, etc.
   */
  className?: string;
  /**
   * Optional render override. When provided, called per queued entry
   * so consumers can render a custom indicator (spinner, retry button
   * for failed entries, etc.). Defaults to a plain `<Chat.UserMessage>`
   * with subtle opacity for `delivering` and a small "queued" hint for
   * `queued`.
   */
  renderItem?: (entry: QueuedMessage) => ReactNode;
}

/**
 * Default visual: standard user bubble, with status-driven affordance.
 *   - delivering: half-opacity (still in flight to the server)
 *   - queued:     full opacity, small "queued" hint underneath
 *   - failed:     red tint + error text
 */
function defaultRender(entry: QueuedMessage): ReactNode {
  const opacity =
    entry.status === "delivering" ? "opacity-60" : "opacity-100";
  const failed = entry.status === "failed";

  return (
    <div className={twMerge("flex flex-col items-end gap-1", opacity)}>
      {/*
        Use `content` prop (not children) so multimodal payloads — text +
        image_url + document blocks — flow through the same MessageImages /
        MessageDocuments rendering paths normal user turns use. Attachment
        thumbnails appear in the queued bubble immediately on submit; when
        the agent drains and the real message arrives via the stream, the
        queued entry is removed and the canonical bubble takes its place.
      */}
      <UserMessage content={entry.content as Parameters<typeof UserMessage>[0]["content"]} />
      {entry.status === "delivering" && (
        <span className="text-xs text-muted-foreground italic">
          delivering…
        </span>
      )}
      {entry.status === "queued" && (
        <span className="text-xs text-muted-foreground italic">queued</span>
      )}
      {failed && (
        <span className="text-xs text-destructive">
          failed: {entry.error ?? "unknown error"}
        </span>
      )}
    </div>
  );
}

export function QueuedMessages({ className, renderItem }: QueuedMessagesProps) {
  const queued = useChatQueuedMessages();
  if (queued.length === 0) return null;

  return (
    <>
      {queued.map((entry) => (
        <div key={entry.id} className={className}>
          {(renderItem ?? defaultRender)(entry)}
        </div>
      ))}
    </>
  );
}

QueuedMessages.displayName = "Chat.QueuedMessages";
